/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { GetCustomerDetailsDocument } from 'src/gql/graphql';
import useStores from 'shared/hooks/use-stores';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { usePayByBankSignupOffer } from 'src/payments/hooks/use-pay-by-bank-signup-offer/use-pay-by-bank-signup-offer';
import { useCashBackIncentives } from 'src/payments/hooks/use-cash-back-incentives';
import { useCashBackSurfacesConfig } from 'src/payments/hooks/use-cash-back-surfaces-config';
import { DUTCHIE_PAY, LOG_NAMESPACES } from 'src/payments/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { ValidIncentiveValue, isValidIncentiveValue, VALID_INCENTIVE_VALUES_ARRAY } from '../shared/incentive-types';

const logger = getLogger(LOG_NAMESPACES.PBB_INCENTIVES);

export enum UserType {
  UNENROLLED = 'unenrolled',
  QUALIFIED = 'qualified',
  NOT_QUALIFIED = 'not-qualified',
}

// Settled userType mapping shared by every incentive-exposure call site (checkout, order
// confirmation, kiosk). Callers gate on their own loading states before firing; this maps
// the resolved values only.
export function resolveIncentiveUserType({
  isLoggedIn,
  isEnrolledDutchiePay,
  successfulOrderCount,
}: {
  isLoggedIn: boolean;
  isEnrolledDutchiePay: boolean;
  successfulOrderCount?: number;
}): UserType {
  if (!isLoggedIn || !isEnrolledDutchiePay) {
    return UserType.UNENROLLED;
  }
  return successfulOrderCount === 0 ? UserType.QUALIFIED : UserType.NOT_QUALIFIED;
}

export type IncentivesResponse = {
  incentivesActive: boolean;
  incentiveValue?: ValidIncentiveValue;
  showPaymentBanner: boolean;
  showCTABanner: boolean;
  userType: UserType;
  qualifiedPBBUser: boolean;
  payerId: string | null;
  isCashBack: boolean;
  shouldShowCashBack: boolean;
  showCashBackRedesign: boolean;
};

export function useIncentives(): IncentivesResponse {
  const flags = useFlags();
  const payByBankIncentivesEnabled = flags['fintech.platform.pay-by-bank-incentives'] ?? false;
  const payerIdEnabled = flags['core.cats-ecomm.use-payer-id-in-price-cart.rollout'] ?? false;
  const { checkout: cashBackCheckoutSurfaceEnabled } = useCashBackSurfacesConfig();

  const { isEnrolledDutchiePay } = useDutchiePay();
  const { Cart, User } = useStores();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const userToken = useObserver(() => User.token);
  const isLoggingIn = useMemo(() => !!userToken && !isLoggedIn, [userToken, isLoggedIn]);
  const menuType = useObserver(() => Cart.menuType);
  const { offer: payByBankIncentive, error: offerError } = usePayByBankSignupOffer({
    dispensaryId: Cart.order?.dispensary?.id,
    menuType,
    enabled: payByBankIncentivesEnabled,
  });

  const { shouldShowBanner: shouldShowCashBackBanner, cashBackIncentiveValue } = useCashBackIncentives();

  if (offerError) {
    logger.error('Error fetching PBB signup offer', {
      error: offerError,
      dispensaryId: Cart.order?.dispensary?.id,
      dispensaryName: Cart.order?.dispensary?.name,
    });
  }

  const isGuestUser = !User?.exists;

  const { data: customerDetailsData, error: customerDetailsError, loading: customerDetailsLoading } = useQuery(
    GetCustomerDetailsDocument,
    {
      skip: !payByBankIncentivesEnabled || isGuestUser || !isEnrolledDutchiePay,
    }
  );

  if (customerDetailsError) {
    logger.error('Error fetching customer details', {
      error: customerDetailsError,
      dispensaryId: Cart.order?.dispensary?.id,
      dispensaryName: Cart.order?.dispensary?.name,
      userId: User?.id,
    });
  }

  // If dispensary PBB incentives are disabled, check if cash-back incentives should still show
  if (!payByBankIncentivesEnabled) {
    return {
      incentivesActive: shouldShowCashBackBanner,
      incentiveValue: cashBackIncentiveValue,
      showPaymentBanner: shouldShowCashBackBanner,
      showCTABanner: shouldShowCashBackBanner && Cart.paymentMethod !== DUTCHIE_PAY,
      userType: UserType.NOT_QUALIFIED,
      qualifiedPBBUser: false,
      payerId: null,
      isCashBack: shouldShowCashBackBanner,
      shouldShowCashBack:
        Cart.paymentMethod === DUTCHIE_PAY && shouldShowCashBackBanner && (cashBackIncentiveValue ?? 0) > 0,
      showCashBackRedesign:
        cashBackCheckoutSurfaceEnabled && shouldShowCashBackBanner && (cashBackIncentiveValue ?? 0) > 0,
    };
  }

  const acceptsPBB = Cart.order?.dispensary?.paymentTypesAccepted?.dutchiePay;
  const qualifiedPBBUser = customerDetailsData?.getCustomerDetails.successfulOrderCount === 0;
  const payerId = payerIdEnabled && qualifiedPBBUser ? customerDetailsData.getCustomerDetails.externalId : null;

  // Transient states (mid-login, details in flight) report NOT_QUALIFIED here; the shared
  // mapping applies once settled. Parity with the pre-existing chain is covered by the
  // use-incentives suite.
  const userType =
    (isGuestUser || !isEnrolledDutchiePay) && (customerDetailsLoading || isLoggingIn)
      ? UserType.NOT_QUALIFIED
      : resolveIncentiveUserType({
          isLoggedIn: !isGuestUser,
          isEnrolledDutchiePay,
          successfulOrderCount: customerDetailsData?.getCustomerDetails.successfulOrderCount,
        });

  // Calculate and validate the POS-reported offer amount before casting.
  const calculatedDispensaryValue = payByBankIncentive?.amount ? Math.round(payByBankIncentive.amount) : undefined;
  const dispensaryIncentiveValue = isValidIncentiveValue(calculatedDispensaryValue)
    ? calculatedDispensaryValue
    : undefined;

  const hasDispensaryIncentive =
    payByBankIncentivesEnabled && !!payByBankIncentive && !!dispensaryIncentiveValue && acceptsPBB;

  // User is eligible for dispensary incentive only if they haven't used PBB before
  const isEligibleForDispensaryIncentive =
    hasDispensaryIncentive && (userType === UserType.UNENROLLED || userType === UserType.QUALIFIED);

  // Use dispensary incentive if user is eligible, otherwise fall back to cash-back incentive
  // Note: isEligibleForDispensaryIncentive already guarantees dispensaryIncentiveValue is defined
  let finalIncentiveValue: ValidIncentiveValue | undefined;
  if (isEligibleForDispensaryIncentive) {
    finalIncentiveValue = dispensaryIncentiveValue;
  } else if (cashBackIncentiveValue !== undefined) {
    finalIncentiveValue = cashBackIncentiveValue;
  }

  // Incentives are active if user is eligible for dispensary incentive OR cash-back
  const incentivesActive = isEligibleForDispensaryIncentive || shouldShowCashBackBanner;
  // It's a cash-back incentive if user isn't eligible for dispensary incentive but cash-back is available
  const isCashBack = !isEligibleForDispensaryIncentive && shouldShowCashBackBanner;

  // Show banner if user is eligible for dispensary incentive OR if cash-back banner should show
  const showPaymentBanner = isEligibleForDispensaryIncentive || shouldShowCashBackBanner;
  const showCTABanner = incentivesActive && Cart.paymentMethod !== DUTCHIE_PAY;

  // Business logic for showing cash-back display in confirmation
  const shouldShowCashBack =
    Cart.paymentMethod === DUTCHIE_PAY && incentivesActive && isCashBack && (finalIncentiveValue ?? 0) > 0;

  if (payByBankIncentive && !dispensaryIncentiveValue) {
    logger.error('Invalid or missing incentive value detected', {
      calculatedValue: calculatedDispensaryValue,
      incentiveValue: dispensaryIncentiveValue,
      dispensaryId: Cart.order?.dispensary?.id,
      validValues: Object.values(VALID_INCENTIVE_VALUES_ARRAY),
    });
  }
  return {
    incentivesActive,
    incentiveValue: finalIncentiveValue,
    showPaymentBanner,
    showCTABanner,
    userType,
    qualifiedPBBUser,
    payerId,
    isCashBack,
    shouldShowCashBack,
    showCashBackRedesign: cashBackCheckoutSurfaceEnabled && isCashBack && (finalIncentiveValue ?? 0) > 0,
  };
}
